import type { RegistrationDto } from "@/lib/types";
import { filterSeasonsByWorkspace } from "@/lib/workspace-scope";

/**
 * The challenge the sidebar's challenge-scoped links open.
 *
 * Someone in several challenges almost always means the one running right now, so that is what the
 * nav opens. Registrations arrive newest-first, which is the fallback once every challenge has
 * finished. The workspace chosen in the header narrows the field first, or the links would open a
 * campaign from a workspace the reader is not looking at.
 */
export function pickActiveSlug(
  registrations: readonly RegistrationDto[],
  selectedWorkspaceId: string | null,
): string | null {
  const inWorkspace = filterSeasonsByWorkspace(registrations, selectedWorkspaceId);
  const running = inWorkspace.find(
    (r) =>
      r.challengeStartDate.slice(0, 10) <= r.challengeCurrentDate &&
      r.challengeCurrentDate <= r.challengeEndDate.slice(0, 10),
  );
  return (running ?? inWorkspace[0])?.challengeSlug ?? null;
}

/**
 * Where to go when the reader picks a different workspace.
 *
 * Filtering the season pickers is not enough on its own: a challenge-scoped page keeps showing the
 * campaign in its URL, so choosing another workspace would leave the header naming one workspace
 * while the whole page reported a campaign from another. When that happens the equivalent page for
 * the new workspace's own campaign is opened instead, falling back to the dashboard when the reader
 * has no campaign there.
 *
 * Returns null to stay put, which covers every case where the page is already telling the truth:
 * a page that is not challenge-scoped, "All workspaces", a campaign already in the new workspace,
 * and a campaign the reader is not registered for — where the URL is the only thing that knows
 * which campaign is meant, so replacing it would lose the page they asked for.
 */
export function pathAfterWorkspaceChange(
  pathname: string,
  registrations: readonly RegistrationDto[],
  nextWorkspaceId: string | null,
): string | null {
  if (!nextWorkspaceId) return null;

  const match = /^\/challenges\/([^/]+)(\/.*)?$/.exec(pathname);
  if (!match) return null;

  const [, slug, tail = ""] = match;
  const current = registrations.find((r) => r.challengeSlug === slug);
  if (!current || !current.workspaceId) return null;
  if (current.workspaceId === nextWorkspaceId) return null;

  const replacement = pickActiveSlug(registrations, nextWorkspaceId);
  return replacement ? `/challenges/${replacement}${tail}` : "/dashboard";
}
